// Central content for the Doug Jones Insurance Agency site.
import {
  Car, Home, HeartPulse, Building2, ShieldCheck, FileText,
  CreditCard, IdCard, PhoneCall, Landmark,
} from "lucide-react";

export const AGENCY = {
  name: "Doug Jones Insurance and Financial Planning Agency",
  legalName: "PDJ Insurance & Financial Services Inc",
  short: "ANICO / TWFG",
  phone: "225-291-3386",
  phoneHref: "tel:+12252913386",
  email: "doug.jones@twfg.com",
  address: "11863 Market Pl Ave, Baton Rouge, LA 70816",
  addressMapQuery: "11863+Market+Pl+Ave,+Baton+Rouge,+LA+70816",
  hours: [
    { d: "Monday – Friday", h: "8:30 AM – 5:30 PM" },
    { d: "Saturday", h: "By Appointment" },
    { d: "Sunday", h: "Closed" },
  ],
  license: "LA Dept. of Insurance Lic. #474515",
  years: 20,
};

export const SMS_CONSENT = {
  version: "v1.1-2026-06",
  text: "I consent to receive SMS text messages from Doug Jones Insurance and Financial Planning Agency (PDJ Insurance & Financial Services Inc) about my quote, policy, and account. Message frequency varies. Message & data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our Privacy Policy and Terms of Service.",
};

export const PRODUCTS = [
  {
    slug: "auto",
    icon: Car,
    title: "Auto",
    tagline: "Coverage for every mile of the bayou.",
    desc: "Liability, collision, comprehensive, and full-coverage options tailored to Louisiana roads and rates.",
    points: ["Liability & full coverage", "Uninsured motorist", "Roadside & rental", "Multi-car discounts"],
    image: "https://images.unsplash.com/photo-1626722233326-20a812be15ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    slug: "home",
    icon: Home,
    title: "Home",
    tagline: "Protect the place your family calls home.",
    desc: "Homeowners, renters, flood, and windstorm protection built for Gulf Coast weather realities.",
    points: ["Homeowners & renters", "Flood & windstorm", "Personal property", "Bundled savings"],
    image: "https://images.unsplash.com/photo-1709787627975-9cb37bbeca60?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    slug: "life",
    icon: HeartPulse,
    title: "Life",
    tagline: "Peace of mind that outlasts you.",
    desc: "Term, whole, and final expense policies that keep the people you love financially secure.",
    points: ["Term & whole life", "Final expense", "Annuities", "Family protection"],
    image: "https://images.unsplash.com/photo-1780329946888-0db2441b249c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
  {
    slug: "commercial",
    icon: Building2,
    title: "Commercial",
    tagline: "Insurance that works as hard as you do.",
    desc: "General liability, commercial property, workers' comp, and BOP coverage for Baton Rouge businesses.",
    points: ["General liability", "Commercial property", "Workers' comp", "Business owners policy"],
    image: "https://images.unsplash.com/photo-1764173039192-2bbd508d5211?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  },
];

export const CARRIERS = [
  "American National", "Progressive", "Nationwide", "Travelers",
  "Safeco", "Foremost", "Allstate", "Hartford", "Encompass", "National General",
];

export const STATS = [
  { value: "20+", label: "Years serving Louisiana" },
  { value: "4.9", label: "Average client rating" },
  { value: "10+", label: "Carrier partners" },
  { value: "1", label: "Local family you can call" },
];

export const MANIFESTO = [
  { n: "01", title: "Local, not a call center", body: "We live and work in Baton Rouge. When a storm rolls in off the Gulf, we're your neighbors, not a 1-800 number." },
  { n: "02", title: "Independent by design", body: "As an independent agency we shop 10+ carriers for you — so you get the right coverage at the right price, not a one-size policy." },
  { n: "03", title: "Straight talk on coverage", body: "No jargon, no upselling. We explain exactly what you're covered for and what you're not, in plain English." },
  { n: "04", title: "Here when it matters", body: "Claims, changes, questions at 4:45 on a Friday — you get a real person who knows your name and your policy." },
];

export const TEAM = [];

export const TESTIMONIALS = [
  { name: "Rebecca T.", city: "Baton Rouge, LA", stars: 5, quote: "Doug's team saved us over $600 a year and actually explained our flood coverage. Felt like family, not a transaction." },
  { name: "Marcus & Dana L.", city: "Prairieville, LA", stars: 5, quote: "When the storm hit, Camille had our claim moving within an hour. You can't put a price on that kind of service." },
  { name: "The Hebert Family", city: "Denham Springs, LA", stars: 5, quote: "Bundled our home, auto, and life in one visit. Simple, honest, and they answer the phone every time." },
  { name: "J. Ory, Owner", city: "Baton Rouge, LA", stars: 5, quote: "As a small business owner, Andre found me the right liability coverage without the runaround. Highly recommend." },
];

export const SELF_SERVICE = [
  { icon: CreditCard, title: "Pay My Bill", desc: "Make a secure payment through your carrier's payment portal.", cta: "Pay online", href: "#contact" },
  { icon: IdCard, title: "Download ID Cards", desc: "Access proof of insurance and digital ID cards anytime.", cta: "Get ID cards", href: "#contact" },
  { icon: FileText, title: "Request a Policy Change", desc: "Add a vehicle, driver, or update coverage in minutes.", cta: "Request change", href: "#quote" },
  { icon: PhoneCall, title: "File a Claim", desc: "Report a claim 24/7 and reach carrier claims lines fast.", cta: "Start a claim", href: "#claims" },
];

export const CLAIMS_LINES = [
  { carrier: "American National", phone: "1-800-333-2860" },
  { carrier: "Progressive", phone: "1-800-776-4737" },
  { carrier: "Nationwide", phone: "1-800-421-3535" },
  { carrier: "Travelers", phone: "1-800-252-4633" },
];

export const FAQS = [
  { q: "Do I need flood insurance in Baton Rouge?", a: "In most of the Baton Rouge area, standard homeowners policies do not cover flood damage. Given our proximity to the Gulf and past flooding, we strongly recommend a separate flood policy. We'll review your flood zone and options with you." },
  { q: "What does it mean that you're an independent agency?", a: "We aren't tied to a single insurance company. We represent 10+ carriers, which means we shop your coverage across all of them to find the best combination of price and protection for your situation." },
  { q: "Can I bundle home, auto, and life?", a: "Absolutely — and you usually should. Bundling multiple policies with the same carrier typically unlocks meaningful multi-policy discounts. We'll quote bundled and separate options so you can compare." },
  { q: "How do I file a claim?", a: "You can call us directly at 225-291-3386 and we'll walk you through it, or use the carrier claims line listed in our Claims section. Most carriers also offer 24/7 online and phone reporting." },
  { q: "What is the SMS opt-in on your forms?", a: "It lets us text you updates about your quote or policy. It's completely optional and complies with A2P 10DLC regulations — you can opt out anytime by replying STOP." },
];

export const TRUST = [
  { icon: ShieldCheck, title: "Licensed in Louisiana", desc: "Fully licensed and compliant with the LA Department of Insurance." },
  { icon: Landmark, title: "20+ Years Local", desc: "Two decades of serving Baton Rouge families and businesses." },
  { icon: FileText, title: "Independent Agency", desc: "We work for you, comparing 10+ trusted carriers." },
];
